import * as THREE from "three";

/**
 * Helpers for dropping arbitrary GLTF models into a game built around a 2-unit
 * tile grid. Source packs come in all sizes (KayKit tiles are 4 units, Kenney
 * characters are 2), so we auto-fit at runtime instead of hardcoding scales.
 */

/** Uniformly scale an object so its height == targetH, with feet at y=0. */
export function fitToHeight(obj, targetH) {
  obj.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(obj);
  const size = new THREE.Vector3();
  box.getSize(size);
  if (size.y > 0) {
    const s = targetH / size.y;
    obj.scale.multiplyScalar(s);
    obj.position.y -= box.min.y * s; // drop feet to the ground plane
  }
  return obj;
}

/**
 * Build a single InstancedMesh from a tile model, repeated across grid cells,
 * scaled so the tile footprint fills one `tile`-sized cell and its top sits at
 * y≈0. Bakes the source node transform into the geometry so orientation is
 * preserved. Returns null if the model has no mesh.
 *
 * cells: array of [gx, gy].
 */
export function instancedTiles(sourceObj, cells, tile, elevFn = null) {
  sourceObj.updateWorldMatrix(true, true);
  let mesh = null;
  sourceObj.traverse((o) => { if (!mesh && o.isMesh) mesh = o; });
  if (!mesh) return null;

  const geo = mesh.geometry.clone();
  geo.applyMatrix4(mesh.matrixWorld); // bake node transform
  geo.computeBoundingBox();
  const bb = geo.boundingBox;
  const sx = bb.max.x - bb.min.x, sz = bb.max.z - bb.min.z;
  const footprint = Math.max(sx, sz) || 1;
  const scale = tile / footprint;
  const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;

  const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
  const inst = new THREE.InstancedMesh(geo, material, cells.length);
  inst.receiveShadow = true;
  inst.castShadow = false;

  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const scl = new THREE.Vector3(scale, scale, scale);
  const pos = new THREE.Vector3();
  let i = 0;
  for (const [gx, gy] of cells) {
    const ey = elevFn ? elevFn(gx, gy) : 0;
    pos.set(gx * tile - cx * scale, -bb.max.y * scale + ey, gy * tile - cz * scale);
    m.compose(pos, q, scl);
    inst.setMatrixAt(i++, m);
  }
  inst.instanceMatrix.needsUpdate = true;
  return inst;
}
